import { Box, Card, CardContent, Paper, Skeleton, TableCell, TableRow } from '@mui/material';
import { CARD_SX } from './tableChrome';
import { balancedGridColumns, twoRowGridColumn } from './kpiLayout';

/**
 * Loading skeletons for the SIOP cockpit, shown while the backend parses the
 * source workbooks at boot (the /data/status probe reports state="loading").
 *
 * A skeleton's whole job is to be the SAME SHAPE as the thing replacing it —
 * otherwise the page jumps when the data lands, which is the layout shift the
 * skeleton was supposed to prevent. So each of these is built from the same
 * chrome tokens (PANEL_SX / CARD_SX) and the same dimensions as its real
 * counterpart, and the numbers below are read off those components rather than
 * chosen to look about right:
 *
 *   KpiCards        5 cards, flex 1 1 180px, minWidth 160, minHeight 68, p 1.5,
 *                   a 12px label and a 16px figure
 *   PulseBridgeKpiRow  a grid of minmax(150px, 1fr) tiles, gap 1.5, p 1.5
 *   PulseBridgeWaterfallChart  height 380
 *
 * If one of those changes, the matching number here has to change with it.
 */

/** Mirrors KpiCards — five cards by default, because it renders five. */
export const KpiCardsSkeleton = ({ count = 5 }) => (
  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
    {Array.from({ length: count }).map((_, i) => (
      <Box
        key={i}
        sx={{
          flex: '1 1 180px',
          minWidth: 160,
          minHeight: 68,
          p: 1.5,
          display: 'flex',
          flexDirection: 'column',
          ...CARD_SX,
        }}
      >
        {/* The label sits at the top and the figure at the bottom — `mb: auto`
            on the real label is what pushes them apart, so the gap is real
            rather than a guessed margin. */}
        <Skeleton variant="text" width="62%" height={12} sx={{ mb: 0.5 }} />
        <Skeleton variant="text" width="55%" height={16} />
      </Box>
    ))}
  </Box>
);

/** Mirrors PulseBridgeKpiRow: same grid, same tile padding, same type sizes. */
export const PulseKpiRowSkeleton = ({ count = 14, lgMaxColumns, twoRows = false }) => (
  <Box
    sx={{
      display: 'grid',
      // Same balanced-column rule as the real row, so the skeleton wraps at the
      // same points and the page doesn't reflow when the figures land.
      gridTemplateColumns: balancedGridColumns(count, { lgMax: lgMaxColumns, twoRows }),
      gap: 1.5,
      mb: 2,
    }}
  >
    {Array.from({ length: count }).map((_, i) => (
      <Card
        key={i}
        sx={{
          ...CARD_SX,
          ...(twoRows && { gridColumn: { lg: twoRowGridColumn(count, i) } }),
        }}
      >
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Skeleton variant="text" width="70%" height={12} />
          <Skeleton variant="text" width="60%" height={16} />
        </CardContent>
      </Card>
    ))}
  </Box>
);

/**
 * Mirrors a chart. `height` defaults to the waterfall's own 380 — a chart is
 * the one element whose skeleton must be exactly right, since it is the tallest
 * thing on the page and any error moves everything below it.
 */
export const ChartSkeleton = ({ height = 380 }) => (
  <Skeleton variant="rounded" height={height} sx={{ borderRadius: '8px' }} />
);

export const TableSkeleton = ({ rows = 8, cols = 6 }) => (
  <Paper variant="outlined" sx={{ overflow: 'hidden', p: 1.5 }}>
    {/* header bar */}
    <Skeleton variant="rounded" height={32} sx={{ mb: 1 }} />
    {Array.from({ length: rows }).map((_, r) => (
      <Box key={r} sx={{ display: 'flex', gap: 1, mb: 0.75 }}>
        {Array.from({ length: cols }).map((_, c) => (
          <Skeleton key={c} variant="text" height={20} sx={{ flex: c === 0 ? '2 1 0' : '1 1 0' }} />
        ))}
      </Box>
    ))}
  </Paper>
);

/** Table-body-only placeholder for tables whose chrome and headers are static. */
export const TableRowsSkeleton = ({ rows = 8, cols = 6 }) => (
  <>
    {Array.from({ length: rows }).map((_, r) => (
      <TableRow key={r} data-testid="table-row-skeleton" aria-hidden="true">
        {Array.from({ length: cols }).map((__, c) => (
          <TableCell key={c}>
            <Skeleton variant="text" height={20} sx={{ width: c === 0 ? '78%' : '64%' }} />
          </TableCell>
        ))}
      </TableRow>
    ))}
  </>
);

/** Stable form/dialog body placeholder; callers choose the loaded form height. */
export const FormSkeleton = ({ rows = 5, height }) => (
  <Box sx={{ minHeight: height, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
    {Array.from({ length: rows }).map((_, i) => (
      <Skeleton
        key={i}
        variant="rounded"
        height={i === rows - 1 ? 72 : 40}
        sx={{ width: '100%' }}
      />
    ))}
  </Box>
);

/** Full viewport fallback with stable header/content bands instead of a spinner. */
export const RouteSkeleton = () => (
  <Box
    sx={{ minHeight: '100vh', p: { xs: 2, md: 3 }, bgcolor: '#F8F8F8' }}
    role="status"
    aria-label="Loading page"
  >
    <Skeleton variant="rounded" height={48} sx={{ mb: 1.5, borderRadius: 1 }} />
    <Skeleton variant="rounded" height={40} sx={{ mb: 3, borderRadius: 1 }} />
    <Skeleton variant="rounded" height={42} sx={{ mb: 2, borderRadius: 2 }} />
    <TableSkeleton rows={8} cols={6} />
  </Box>
);

/** Content-region fallback used after the authenticated dashboard shell mounts. */
export const RouteContentSkeleton = () => (
  <Box sx={{ p: { xs: 2, md: 3 }, minHeight: 220 }} role="status" aria-label="Loading page content">
    <Skeleton variant="rounded" height={48} sx={{ borderRadius: 2 }} />
  </Box>
);

/** Mirrors the analytics row under the bridge: two insight cards + commentary. */
const InsightsRowSkeleton = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: { xs: 'column', lg: 'row' },
      gap: 2,
      mt: 2,
      alignItems: 'stretch',
    }}
  >
    {Array.from({ length: 3 }).map((_, i) => (
      <Card
        key={i}
        sx={{
          flex: 1,
          minWidth: 280,
          borderRadius: '16px',
          border: '1px solid #D1D5DB',
          boxShadow: 'none',
        }}
      >
        <CardContent>
          <Skeleton variant="text" width="45%" height={22} sx={{ mb: 1.5 }} />
          {Array.from({ length: 5 }).map((__, j) => (
            <Skeleton
              key={j}
              variant="rounded"
              height={46}
              sx={{ mb: 1.25, borderRadius: '10px' }}
            />
          ))}
        </CardContent>
      </Card>
    ))}
  </Box>
);

/**
 * The Pulse pages: one card holding a title, the KPI tile grid, the waterfall
 * and the analytics row. Same nesting as the real page, so nothing shifts when
 * it resolves.
 */
export const PulseBridgeSkeleton = ({ tiles = 14 }) => (
  <Card sx={CARD_SX}>
    <CardContent>
      <Skeleton variant="text" width={220} height={26} sx={{ mb: 1.5 }} />
      <PulseKpiRowSkeleton count={tiles} />
      <ChartSkeleton />
      <InsightsRowSkeleton />
    </CardContent>
  </Card>
);

/**
 * The body placeholder for one route.
 *
 * It takes the SECTION because the cockpit's pages are not the same shape as
 * each other: a table page and the Pulse bridge share nothing but a container.
 * The old single "KPI strip + table" skeleton was shown for all of them, which
 * on the Pulse pages drew a table where a chart was about to appear — and, on
 * Overview, a SECOND KPI strip under the one the layout had already drawn.
 */
export const SiopBodySkeleton = ({ section }) => {
  if (section === 'pulse-bridge') return <PulseBridgeSkeleton tiles={14} />;
  // The products bridge carries fewer levers, so fewer tiles.
  if (section === 'products-pulse') return <PulseBridgeSkeleton tiles={9} />;
  if (section === 'overview') {
    // KPIs are drawn by the layout above this, not here.
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Skeleton variant="rounded" height={92} sx={{ borderRadius: '12px' }} />
        <ChartSkeleton height={320} />
        <TableSkeleton rows={6} />
      </Box>
    );
  }
  if (section === 'field-guide') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} variant="rounded" height={180} sx={{ borderRadius: '12px' }} />
        ))}
      </Box>
    );
  }
  // Every remaining page is a table page (By Category, Sub-Brand, SC Zone, STD
  // Cost, Lookup, AOP Plan). Wider than the default: those tables are quarter
  // columns, not six.
  return <TableSkeleton rows={10} cols={8} />;
};

/** Kept for callers that want the generic pair. */
export const CockpitSkeleton = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <KpiCardsSkeleton />
    <TableSkeleton />
  </Box>
);

export default CockpitSkeleton;
